import { Config } from '../config/config.js';
import { isUserLoggedIn } from '../utils/is-logged-in.js';
import { resolveFetch as resolveFormFetch, submitFormRequest } from './form-submit.js';

const sharedConfig = new Config();
if (typeof window !== 'undefined' && typeof window.__agpProjectsGatewayListCount !== 'number') {
  window.__agpProjectsGatewayListCount = 0;
}
const LIST_CACHE_TTL_MS = 2000;

function normaliseApiBase(value) {
  if (typeof value === 'string' && value.length) {
    return value.endsWith('/') ? value.slice(0, -1) : value;
  }
  return sharedConfig.apiBase.replace(/\/$/, '');
}

function isApiModule(candidate) {
  if (!candidate || typeof candidate !== 'object') {
    return false;
  }
  return (
    typeof candidate.listProjects === 'function' ||
    typeof candidate.createProject === 'function' ||
    typeof candidate.updateProject === 'function' ||
    typeof candidate.deleteProject === 'function' ||
    typeof candidate.uploadProjectFiles === 'function'
  );
}

export class ProjectsGateway {
  constructor(apiOrOptions = null, maybeOptions = {}) {
    let apiCandidate = apiOrOptions;
    let options = maybeOptions;

    if (apiCandidate && !isApiModule(apiCandidate)) {
      options = apiCandidate || {};
      apiCandidate = null;
    }

    if (isApiModule(apiCandidate)) {
      this.api = apiCandidate;
      this.fetch = null;
      this.apiBase = null;
    } else {
      const { fetch: fetchImpl, apiBase } = options || {};
      this.api = null;
      this.fetch = resolveFormFetch(fetchImpl);
      this.apiBase = normaliseApiBase(apiBase);
    }

    this._activeListPromise = null;
    this._lastListResult = null;
    this._lastListTimestamp = 0;
  }

  #getFetch() {
    return this.fetch || resolveFormFetch();
  }

  #resolveUrl(path) {
    const base = this.apiBase || normaliseApiBase();
    return `${base}${path}`;
  }

  #normaliseFiles(files = []) {
    if (!files) {
      return [];
    }
    if (Array.isArray(files)) {
      return files.filter((file) => file != null);
    }
    if (typeof files[Symbol.iterator] === 'function') {
      return Array.from(files).filter((file) => file != null);
    }
    return [files];
  }

  async list() {
    if (this.api) {
      if (typeof this.api.listProjects !== 'function') {
        return [];
      }
      try {
        const projects = await this.api.listProjects();
        return Array.isArray(projects) ? projects : [];
      } catch (error) {
        return [];
      }
    }

    const now = Date.now();
    if (
      this._lastListResult &&
      now - this._lastListTimestamp < LIST_CACHE_TTL_MS
    ) {
      return Array.isArray(this._lastListResult)
        ? this._lastListResult.slice()
        : [];
    }

    if (this._activeListPromise) {
      return this._activeListPromise;
    }

    this._activeListPromise = (async () => {
      const result = await this.#fetchProjects();
      this._lastListResult = Array.isArray(result) ? result.slice() : [];
      this._lastListTimestamp = Date.now();
      return this._lastListResult.slice();
    })();

    try {
      return await this._activeListPromise;
    } finally {
      this._activeListPromise = null;
    }
  }

  async #fetchProjects() {
    const fetch = this.#getFetch();
    if (!fetch) {
      return [];
    }
    try {
      const response = await fetch(this.#resolveUrl('/api/data/projects'), {
        credentials: 'include',
      });
      if (!response || !response.ok) {
        return [];
      }
      const payload = await response.json();
      if (typeof window !== 'undefined') {
        window.__agpProjectsGatewayListCount =
          (window.__agpProjectsGatewayListCount || 0) + 1;
      }
      return Array.isArray(payload) ? payload : [];
    } catch (error) {
      return [];
    }
  }

  async create({ name, instructions = '', files = [], color = null, teamType = null } = {}) {
    if (this.api) {
      if (typeof this.api.createProject !== 'function') {
        throw new Error('Projects API unavailable');
      }
      const project = await this.api.createProject({ name, instructions, color, teamType });
      await this.#maybeUploadFilesWithApi(project?.id, files);
      return project;
    }

    const fetch = this.#getFetch();
    if (!fetch) {
      throw new Error('Projects gateway requires fetch');
    }

    const trimmedName = typeof name === 'string' ? name.trim() : '';
    if (!trimmedName) {
      throw new Error('Project name is required');
    }

    const payload = { name: trimmedName };
    if (typeof instructions === 'string' && instructions.trim()) {
      payload.instructions = instructions.trim();
    }
    if (color) payload.color = color;
    if (teamType) payload.teamType = teamType;

    const response = await fetch(this.#resolveUrl('/api/data/projects'), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify(payload),
    });

    if (!response || !response.ok) {
      const errorBody = await response.json().catch(() => ({}));
      throw new Error(errorBody.error || `Create project failed: ${response?.status ?? 'error'}`);
    }

    const project = await response.json();
    await this.#maybeUploadFiles(project?.id, files);
    return project;
  }

  async update(projectId, data = {}) {
    if (this.api) {
      if (typeof this.api.updateProject !== 'function') {
        throw new Error('Projects API unavailable');
      }
      return this.api.updateProject(projectId, data);
    }

    if (!projectId) {
      throw new Error('Project ID is required for update');
    }

    const fetch = this.#getFetch();
    if (!fetch) {
      throw new Error('Projects gateway requires fetch');
    }

    const response = await fetch(this.#resolveUrl(`/api/data/projects/${encodeURIComponent(projectId)}`), {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify(data || {}),
    });

    if (!response || !response.ok) {
      const errorBody = await response.json().catch(() => ({}));
      throw new Error(errorBody.error || 'Failed to update project');
    }

    return response.json();
  }

  async delete(projectId) {
    if (this.api) {
      if (typeof this.api.deleteProject !== 'function') {
        throw new Error('Projects API unavailable');
      }
      return this.api.deleteProject(projectId);
    }

    if (!projectId) {
      throw new Error('Project ID is required');
    }

    const fetch = this.#getFetch();
    if (!fetch) {
      throw new Error('Projects gateway requires fetch');
    }

    const response = await fetch(this.#resolveUrl(`/api/data/projects/${encodeURIComponent(projectId)}`), {
      method: 'DELETE',
      credentials: 'include',
    });

    if (!response || !response.ok) {
      const errorBody = await response.json().catch(() => ({}));
      throw new Error(errorBody.error || 'Failed to delete project');
    }

    return undefined;
  }

  async listProjectFiles(projectId) {
    if (this.api) {
      if (typeof this.api.listProjectFiles === 'function') {
        return this.api.listProjectFiles(projectId);
      }
      throw new Error('Projects API unavailable');
    }

    if (!projectId) {
      throw new Error('Project ID is required');
    }

    const fetch = this.#getFetch();
    if (!fetch) {
      throw new Error('Projects gateway requires fetch');
    }

    const response = await fetch(
      this.#resolveUrl(`/api/data/projects/${encodeURIComponent(projectId)}/files`),
      { credentials: 'include' }
    );

    if (!response || !response.ok) {
      const errorBody = await response.json().catch(() => ({}));
      throw new Error(errorBody.error || 'Failed to list project files');
    }

    const rows = await response.json();
    if (!Array.isArray(rows)) {
      return [];
    }

    return rows.map((file, index) => {
      const resolvedName =
        typeof file.name === 'string' && file.name.trim()
          ? file.name.trim()
          : typeof file.file_name === 'string' && file.file_name.trim()
          ? file.file_name.trim()
          : `Attachment ${index + 1}`;
      const resolvedUrl =
        typeof file.url === 'string' && file.url.trim()
          ? file.url.trim()
          : typeof file.downloadUrl === 'string' && file.downloadUrl.trim()
          ? file.downloadUrl.trim()
          : typeof file.download_url === 'string' && file.download_url.trim()
          ? file.download_url.trim()
          : this.#resolveUrl(
              `/api/data/projects/${encodeURIComponent(projectId)}/files/${encodeURIComponent(file.id)}`
            );
      const sizeValue = file.size ?? file.file_size ?? null;
      const size =
        typeof sizeValue === 'number'
          ? sizeValue
          : sizeValue != null && !Number.isNaN(Number(sizeValue))
          ? Number(sizeValue)
          : null;
      const uploadedAt =
        file.uploadedAt ?? file.created_at ?? file.createdAt ?? null;
      const mimeType = file.mimeType ?? file.mime_type ?? null;

      return {
        id: file.id,
        name: resolvedName,
        mimeType,
        size,
        uploadedAt,
        url: resolvedUrl,
        raw: file,
      };
    });
  }

  async uploadFiles(projectId, files = []) {
    if (this.api) {
      if (typeof this.api.uploadProjectFiles !== 'function') {
        throw new Error('Projects API unavailable');
      }
      const normalizedFiles = this.#normaliseFiles(files);
      return this.api.uploadProjectFiles(projectId, normalizedFiles);
    }

    if (!projectId) {
      throw new Error('Project ID is required');
    }

    const fetch = this.#getFetch();
    if (!fetch) {
      throw new Error('Projects gateway requires fetch');
    }

    const normalizedFiles = this.#normaliseFiles(files);
    if (!normalizedFiles.length) {
      return undefined;
    }

    try {
      await submitFormRequest({
        fetch,
        url: this.#resolveUrl(`/api/data/projects/${encodeURIComponent(projectId)}/files`),
        files: normalizedFiles,
      });
    } catch (error) {
      const err = new Error(error?.message || 'Upload failed');
      err.cause = error;
      throw err;
    }

    return undefined;
  }

  async deleteProjectFile(projectId, fileId) {
    if (this.api) {
      if (typeof this.api.deleteProjectFile === 'function') {
        return this.api.deleteProjectFile(projectId, fileId);
      }
      throw new Error('Projects API unavailable');
    }

    if (!projectId || !fileId) {
      throw new Error('Project and file IDs are required');
    }

    const fetch = this.#getFetch();
    if (!fetch) {
      throw new Error('Projects gateway requires fetch');
    }

    const response = await fetch(
      this.#resolveUrl(`/api/data/projects/${encodeURIComponent(projectId)}/files/${encodeURIComponent(fileId)}`),
      {
        method: 'DELETE',
        credentials: 'include',
      }
    );

    if (!response || !response.ok) {
      const errorBody = await response.json().catch(() => ({}));
      throw new Error(errorBody.error || 'Failed to delete project file');
    }

    return undefined;
  }

  async #maybeUploadFiles(projectId, files) {
    const normalizedFiles = this.#normaliseFiles(files);
    if (!projectId || !normalizedFiles.length) {
      return;
    }
    try {
      await this.uploadFiles(projectId, normalizedFiles);
    } catch (error) {
      // Ignore upload failures to mirror legacy behaviour
    }
  }

  async #maybeUploadFilesWithApi(projectId, files) {
    const normalizedFiles = this.#normaliseFiles(files);
    if (!projectId || !normalizedFiles.length || !this.api || typeof this.api.uploadProjectFiles !== 'function') {
      return;
    }
    try {
      await this.api.uploadProjectFiles(projectId, normalizedFiles);
    } catch (error) {
      // Ignore upload failures to mirror legacy behaviour
    }
  }
}

if (typeof window !== 'undefined') {
  window.AgileProGateways = window.AgileProGateways || {};
  window.AgileProGateways.ProjectsGateway = ProjectsGateway;
}
